// ABOUTME: Orchestration hook for the Order Intake Lead Time entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'
import { useOrderIntakeLeadTimeConfig } from './useOrderIntakeLeadTimeConfig'
import { useCreateOrderIntakeLeadTime, useCreateOrderIntakeLeadTimeWithScope, useUpdateOrderIntakeLeadTime, useDeleteOrderIntakeLeadTime } from './useOrderIntakeLeadTimeMutations'
import { useBusinessDays } from './useBusinessDays'
import { OrderIntakeLeadTimeConfig, OrderIntakeLeadTimeFormValues, OrderIntakeLeadTimeOverrides } from '../types/orderIntakeLeadTime'
import { DayIdentifier } from '../types/deliveryWindow'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseOrderIntakeLeadTimeEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: OrderIntakeLeadTimeFormValues) => void
}

interface UseOrderIntakeLeadTimeEntityResult {
  config: OrderIntakeLeadTimeConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
  nonBusinessDays: DayIdentifier[]
}

const equalsParent = (values: OrderIntakeLeadTimeFormValues, config: OrderIntakeLeadTimeConfig): boolean =>
  JSON.stringify(values) === JSON.stringify(config.parentValues)

export const useOrderIntakeLeadTimeEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseOrderIntakeLeadTimeEntityParams): UseOrderIntakeLeadTimeEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading: isConfigLoading, error } = useOrderIntakeLeadTimeConfig(countryId, businessLine, configScopeId, rootScopeId)
  const config = useMemo(() => toPendingScopeConfig(rawConfig, pendingScope, (cfg) => ({
    farDistanceThreshold: cfg.farDistanceThreshold,
    farDistanceThresholdUomId: cfg.farDistanceThresholdUomId ?? '',
    farDistanceThresholdUomDisplay: cfg.farDistanceThresholdUomDisplay,
    standard: cfg.standard,
    nonStandard: cfg.nonStandard,
    distanceUomOptions: cfg.distanceUomOptions,
  })), [rawConfig, pendingScope])
  const { nonBusinessDays, isLoading: isBusinessDaysLoading } = useBusinessDays(countryId, businessLine, configScopeId, rootScopeId)

  const createMutation = useCreateOrderIntakeLeadTime(countryId, businessLine)
  const createWithScopeMutation = useCreateOrderIntakeLeadTimeWithScope(countryId, businessLine)
  const updateMutation = useUpdateOrderIntakeLeadTime(countryId, businessLine)
  const deleteMutation = useDeleteOrderIntakeLeadTime(countryId, businessLine)

  const formValuesRef = useRef<OrderIntakeLeadTimeFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<OrderIntakeLeadTimeConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: OrderIntakeLeadTimeFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !formValuesRef.current.standard || !configRef.current) return
      const values = formValuesRef.current
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (equalsParent(values, configRef.current)) {
          onSuccess({})
          return
        }
        const overrides: OrderIntakeLeadTimeOverrides = { farDistanceThreshold: true, leadTimes: true }
        mutationsRef.current.createWithScopeMutation.mutate({ scope: scopeToCreate, values, overrides }, { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) })
        return
      }

      if (!scopeId) return

      if (scopeId !== rootScopeId && equalsParent(values, configRef.current)) {
        if (configIdRef.current) {
          mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      const overrides: OrderIntakeLeadTimeOverrides = {
        farDistanceThreshold: true,
        leadTimes: true,
      }
      const isNew = !configIdRef.current
      const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
      mutate({ scopeId, values, overrides }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId]
  )

  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId]
  )

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending
  const isLoading = isConfigLoading || isBusinessDaysLoading

  return {
    config,
    isLoading,
    error,
    isSaving,
    actions,
    nonBusinessDays,
  }
}
